import React, { useEffect, useState } from "react";
import { PageHeaderMui } from "../../../components/index.js";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { ShieldPersonIcon } from "../../../components/mui/index.js";
import EnhancedTable from "../../../components/mui/enhanced-table/enhanced-table.component.js";
import moment from "moment";
import { getVoiceAgentAccounts } from "../../../shared/billing.api.js";
import { AccountDetailsModal } from "./account-details-modal.component.js";

const STATUS_CHIP = {
  active: { label: "Active", bg: "#DCFCE7", color: "#166534" },
  needs_setup: { label: "Needs Setup", bg: "#FEF3C7", color: "#92400E" },
  cancellation_scheduled: {
    label: "Cancellation Scheduled",
    bg: "#FEF3C7",
    color: "#92400E",
  },
  attention: {
    label: "Needs Attention",
    bg: "#FEE2E2",
    color: "#991B1B",
  },
  no_agents: { label: "No Agents", bg: "#F3F4F6", color: "#6B7280" },
};

const TRIAL_STATE_CHIP = {
  not_started: { label: "Not Started", bg: "#F3F4F6", color: "#6B7280" },
  trial_active: { label: "Trial Active", bg: "#FEF3C7", color: "#92400E" },
  trial_complete: { label: "Trial Ended", bg: "#F3F4F6", color: "#6B7280" },
  subscription_canceled: { label: "Canceled", bg: "#FEE2E2", color: "#991B1B" },
  paid: { label: "Paid", bg: "#DCFCE7", color: "#166534" },
  exempt: { label: "Exempt (Internal)", bg: "#DBEAFE", color: "#1E40AF" },
};

const FINDING_SEVERITY_CHIP = {
  critical: { bg: "#FEE2E2", color: "#991B1B" },
  high: { bg: "#FECACA", color: "#991B1B" },
  warning: { bg: "#FEF3C7", color: "#92400E" },
  medium: { bg: "#FEF3C7", color: "#92400E" },
  low: { bg: "#DBEAFE", color: "#1E40AF" },
  info: { bg: "#DBEAFE", color: "#1E40AF" },
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_CHIP[status] || STATUS_CHIP.no_agents;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: "11px",
        height: "22px",
      }}
    />
  );
};

const TrialStateChip = ({ state }) => {
  const cfg = TRIAL_STATE_CHIP[state] || {
    label: state || "—",
    bg: "#F3F4F6",
    color: "#6B7280",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: "11px",
        height: "20px",
      }}
    />
  );
};

const AuditFindingsChips = ({ findings }) => {
  if (!findings?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
      {findings.map((f, i) => {
        const cfg =
          FINDING_SEVERITY_CHIP[(f.severity || "").toLowerCase()] ||
          FINDING_SEVERITY_CHIP.info;
        return (
          <Tooltip key={`${f.code}:${i}`} title={f.message}>
            <Chip
              label={f.code.replace(/_/g, " ")}
              size="small"
              sx={{
                bgcolor: cfg.bg,
                color: cfg.color,
                fontWeight: 600,
                fontSize: "10px",
                height: "18px",
                textTransform: "capitalize",
              }}
            />
          </Tooltip>
        );
      })}
    </Stack>
  );
};

const deriveAccountStatus = (row) => {
  const findings = row.audit_findings || [];
  const hasBlocker = findings.some((f) => {
    const sev = (f.severity || "").toLowerCase();
    return sev === "critical" || sev === "high";
  });
  if (hasBlocker) return "attention";

  const agents = (row.companies || []).flatMap((c) => c.agents || []);
  if (agents.length === 0) return "no_agents";
  if (agents.some((a) => a.status === "cancellation_scheduled"))
    return "cancellation_scheduled";
  if (agents.some((a) => a.status === "needs_setup")) return "needs_setup";
  return "active";
};

const formatMinutes = (used, included, overage, isBilledOverage = false) => {
  if (!included) return used ? `${used} min used` : "—";
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography sx={{ fontSize: "13px" }}>
        {used} / {included}
      </Typography>
      {overage > 0 && (
        <Chip
          label={`+${overage} over`}
          size="small"
          sx={{
            bgcolor: isBilledOverage ? "#DBEAFE" : "#FEE2E2",
            color: isBilledOverage ? "#1E40AF" : "#991B1B",
            fontWeight: 600,
            fontSize: "10px",
            height: "18px",
          }}
        />
      )}
    </Stack>
  );
};

const hasBilledOverage = (row) =>
  Boolean(
    row.contract_line_items?.some(
      (li) => Number(li.overage_price_per_unit) > 0,
    ),
  );

const formatRelative = (iso) => (iso ? moment(iso).fromNow() : "—");
const formatFull = (iso) =>
  iso ? moment(iso).format("MMM D, YYYY h:mm a") : "";

const accountCell = ({ data }) => {
  const findings = data.audit_findings || [];
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ fontWeight: 600 }}>
        {data.account?.name || "—"}
      </Typography>
      {findings.length > 0 && <AuditFindingsChips findings={findings} />}
    </Stack>
  );
};

const contractedHeadCells = [
  {
    key: "name",
    label: "Account",
    width: "22%",
    displayComponent: accountCell,
  },
  {
    key: "trial_state",
    label: "State",
    width: "9%",
    noSort: true,
    displayComponent: ({ data }) => (
      <TrialStateChip state={data.derived?.trial_state} />
    ),
  },
  {
    key: "agents",
    label: "Agents",
    width: "8%",
    noSort: true,
    displayComponent: ({ data }) => (
      <Typography sx={{ fontSize: "13px" }}>
        {data.derived?.configured_agents ?? 0} /{" "}
        {data.derived?.total_agents ?? 0}
      </Typography>
    ),
  },
  {
    key: "purchased_qty",
    label: "Contract Slots",
    width: "13%",
    noSort: true,
    displayComponent: ({ data }) => {
      const qty = data.derived?.purchased_qty ?? 0;
      const items = data.contract_line_items || [];
      if (!items.length) {
        return (
          <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>
            none
          </Typography>
        );
      }
      const tiers = items
        .map(
          (li) =>
            `${parseInt(li.qty, 10)}× ${li.item_key?.replace("voice-agent-", "")}`,
        )
        .join(", ");
      return (
        <Stack>
          <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>
            {qty}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
            {tiers}
          </Typography>
        </Stack>
      );
    },
  },
  {
    key: "minutes",
    label: "Minutes (used / incl)",
    width: "14%",
    noSort: true,
    displayComponent: ({ data }) =>
      formatMinutes(
        data.usage_this_month?.used_minutes ?? 0,
        data.usage_this_month?.included_minutes ?? 0,
        data.usage_this_month?.overage_minutes ?? 0,
        hasBilledOverage(data),
      ),
  },
  {
    key: "installed_at",
    label: "Installed",
    width: "10%",
    displayComponent: ({ data }) => (
      <Tooltip title={formatFull(data.account?.installed_at)}>
        <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
          {formatRelative(data.account?.installed_at)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    key: "last_active",
    label: "Last Call",
    width: "10%",
    displayComponent: ({ data }) => (
      <Tooltip title={formatFull(data.account?.last_active)}>
        <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
          {formatRelative(data.account?.last_active)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "14%",
    noSort: true,
    displayComponent: ({ data }) => (
      <StatusChip status={deriveAccountStatus(data)} />
    ),
  },
];

const plgHeadCells = [
  {
    key: "name",
    label: "Account",
    width: "20%",
    displayComponent: accountCell,
  },
  {
    key: "trial_state",
    label: "State",
    width: "10%",
    noSort: true,
    displayComponent: ({ data }) => (
      <TrialStateChip state={data.derived?.trial_state} />
    ),
  },
  {
    key: "agents",
    label: "Agents",
    width: "8%",
    noSort: true,
    displayComponent: ({ data }) => (
      <Typography sx={{ fontSize: "13px" }}>
        {data.derived?.configured_agents ?? 0} /{" "}
        {data.derived?.total_agents ?? 0}
      </Typography>
    ),
  },
  {
    key: "stripe_status",
    label: "Stripe Sub",
    width: "14%",
    noSort: true,
    displayComponent: ({ data }) => {
      const subs = data.stripe_subscriptions || [];
      if (!subs.length)
        return (
          <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>—</Typography>
        );
      // Collapse same-status subs to a single chip; render distinct statuses
      // separately so a stuck past_due alongside a fresh active doesn't get
      // hidden behind whichever sub happened to sort first.
      const statusToProducts = subs.reduce((acc, s) => {
        const key = s.stripe_status || "—";
        if (!acc[key]) acc[key] = [];
        if (s.product_key)
          acc[key].push(s.product_key.replace("voice-agent-", ""));
        return acc;
      }, {});
      const statuses = Object.entries(statusToProducts);
      return (
        <Stack>
          {statuses.map(([status, products]) => (
            <Typography key={status} sx={{ fontSize: "13px", fontWeight: 600 }}>
              {status}
              {products.length > 0 && (
                <Typography
                  component="span"
                  sx={{ fontSize: "11px", color: "#6B7280", ml: 0.5 }}
                >
                  · {products.join(", ")}
                </Typography>
              )}
            </Typography>
          ))}
          <Typography sx={{ fontSize: "11px", color: "#6B7280" }}>
            qty {data.derived?.purchased_qty ?? 0}
            {subs.length > 1 && ` · ${subs.length} subs`}
          </Typography>
        </Stack>
      );
    },
  },
  {
    key: "minutes",
    label: "Minutes (used / incl)",
    width: "14%",
    noSort: true,
    displayComponent: ({ data }) =>
      formatMinutes(
        data.usage_this_month?.used_minutes ?? 0,
        data.usage_this_month?.included_minutes ?? 0,
        data.usage_this_month?.overage_minutes ?? 0,
        hasBilledOverage(data),
      ),
  },
  {
    key: "installed_at",
    label: "Installed",
    width: "10%",
    displayComponent: ({ data }) => (
      <Tooltip title={formatFull(data.account?.installed_at)}>
        <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
          {formatRelative(data.account?.installed_at)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    key: "last_active",
    label: "Last Call",
    width: "10%",
    displayComponent: ({ data }) => (
      <Tooltip title={formatFull(data.account?.last_active)}>
        <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
          {formatRelative(data.account?.last_active)}
        </Typography>
      </Tooltip>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "14%",
    noSort: true,
    displayComponent: ({ data }) => (
      <StatusChip status={deriveAccountStatus(data)} />
    ),
  },
];

// Filter options key on a params object that merges into the request
// alongside the cohort. Multiple filters can combine in principle, though
// the dropdown is single-select so ops only picks one at a time. Values
// match VoiceAgentAdminFilter fields on the backend one-to-one.
const CONTRACTED_FILTERS = [
  { key: "all", params: {}, label: "All" },
  { key: "has_agents", params: { has_agents: true }, label: "Has agents" },
  { key: "no_agents", params: { has_agents: false }, label: "No agents" },
  { key: "has_contract_line_items", params: { has_contract_line_items: true }, label: "Has contract line items" },
  { key: "in_overage", params: { in_overage: true }, label: "Above included minutes this month" },
  { key: "exempt", params: { exempt: true }, label: "Internal-exempt (Testing/Demo/Sandbox)" },
  { key: "misclassified", params: { misclassified: true }, label: "Misclassified account type" },
];

const PLG_FILTERS = [
  { key: "all", params: {}, label: "All" },
  { key: "has_agents", params: { has_agents: true }, label: "Has agents" },
  { key: "no_agents", params: { has_agents: false }, label: "No agents" },
  { key: "stripe_active", params: { stripe_status: "active" }, label: "Stripe: active" },
  { key: "stripe_past_due", params: { stripe_status: "past_due" }, label: "Stripe: past_due (in dunning)" },
  { key: "stripe_canceled", params: { stripe_status: "canceled" }, label: "Stripe: canceled" },
  { key: "stripe_unpaid", params: { stripe_status: "unpaid" }, label: "Stripe: unpaid" },
  { key: "trial_active", params: { trial_active: true }, label: "Trial active (no paid sub)" },
  { key: "in_overage", params: { in_overage: true }, label: "Above included minutes this month" },
  { key: "exempt", params: { exempt: true }, label: "Internal-exempt (Testing/Demo/Sandbox)" },
  { key: "misclassified", params: { misclassified: true }, label: "Misclassified account type" },
];

export const VoiceAgentAdminList = () => {
  const [tab, setTab] = useState("plg");
  const [renderIteration, setRenderIteration] = useState(0);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filterKey, setFilterKey] = useState("all");
  const [counts, setCounts] = useState({ contracted: null, plg: null });
  //: Filtered row count reported back by EnhancedTable's ``searchCount``
  //: callback. Used to show "X of Y" next to the filter dropdown when a
  //: filter is active — otherwise the tab badge total can mislead ops into
  //: thinking the filter didn't apply.
  const [filteredCount, setFilteredCount] = useState(null);

  // Pull the tab counts so the labels show totals. Cheap (limit=1 each,
  // reads `count` from the paginator). Re-runs whenever ``renderIteration``
  // bumps so the badges stay fresh after a manual table refresh or a tab
  // switch. The contracted tab is currently disabled (the CTA/reap flow
  // is pending sales-team contract-format work), so skip its fetch.
  useEffect(() => {
    let active = true;
    getVoiceAgentAccounts({ cohort: "plg", limit: 1 })
      .then(
        ({ data }) =>
          active && setCounts((c) => ({ ...c, plg: data?.count ?? null })),
      )
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [renderIteration]);

  const isContracted = tab === "contracted";
  const headCells = isContracted ? contractedHeadCells : plgHeadCells;
  const filterOptions = isContracted ? CONTRACTED_FILTERS : PLG_FILTERS;
  const activeFilter = filterOptions.find((f) => f.key === filterKey) || filterOptions[0];

  // Wrapper API that merges the current cohort + filter params into every
  // request. EnhancedTable bumps render iteration when the filter changes
  // via parent re-render via key.
  const wrappedApi = (params) =>
    getVoiceAgentAccounts({
      ...params,
      cohort: tab,
      ...activeFilter.params,
    });

  const tabLabel = (base, count) =>
    count != null ? `${base} (${count})` : base;

  return (
    <>
      <div className="wrapper">
        <PageHeaderMui type="Voice Agent Admin" icon={<ShieldPersonIcon />} />
        <Stack spacing={2}>
          <Box sx={{ px: "20px", pt: "20px" }}>
            <Tabs
              value={tab}
              onChange={(_e, v) => {
                setTab(v);
                setFilterKey("all");
                setRenderIteration((n) => n + 1);
              }}
              sx={{ borderBottom: "1px solid #E5E7EB" }}
            >
              <Tab
                value="contracted"
                label={tabLabel("Contracted Customers", counts.contracted)}
                sx={{ textTransform: "none", fontWeight: 600 }}
                disabled
              />
              <Tab
                value="plg"
                label={tabLabel("PLG Sign-ups", counts.plg)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          <Box
            sx={{ px: "20px", display: "flex", alignItems: "center", gap: 1.5 }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Filter</InputLabel>
              <Select
                label="Filter"
                value={filterKey}
                onChange={(e) => {
                  setFilterKey(e.target.value);
                  setFilteredCount(null);
                  setRenderIteration((n) => n + 1);
                }}
              >
                {filterOptions.map((opt) => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {filterKey !== "all" && filteredCount != null && (
              <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>
                {filteredCount} of{" "}
                {isContracted
                  ? (counts.contracted ?? "—")
                  : (counts.plg ?? "—")}{" "}
                shown
              </Typography>
            )}
          </Box>

          <div className="mui-wrapper">
            {/* Key on tab+filter so EnhancedTable resets pagination + selection cleanly. */}
            <EnhancedTable
              key={`${tab}:${filterKey}`}
              type={
                isContracted
                  ? "Contracted Voice Agent Accounts"
                  : "PLG Voice Agent Sign-ups"
              }
              headCells={headCells}
              api={wrappedApi}
              defaultSort="name"
              renderIteration={renderIteration}
              handleRowClick={(row) => setSelectedRow(row)}
              searchCount={setFilteredCount}
              emptyDataMessage={
                isContracted
                  ? "No contracted accounts have the voice_agent integration installed yet."
                  : "No PLG accounts have the voice_agent integration installed yet."
              }
            />
          </div>
        </Stack>
      </div>

      <AccountDetailsModal
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        row={selectedRow}
      />
    </>
  );
};
